// src/components/CampaignEditorPage.tsx

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
    Container, Box, Button, Paper, Typography, Stack,
    CircularProgress, Alert, Tooltip, IconButton, Snackbar, Divider, Tabs, Tab
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import RuleBuilder from './RuleBuilder';
import PathBuilder from './PathBuilder';
import RuleListItem from './RuleListItem'; // NUEVO: Importamos el nuevo componente

// --- INTERFACES (Sin cambios) ---
export interface Offer { id: number; name: string; }
export interface LandingPage { id: number; name: string; }
export interface Campaign { id: number; name: string; }
export interface PathCondition { id?: number; dimension: string; operator: 'IS' | 'IS_NOT' | 'CONTAINS'; value: string; }
export interface PathItem { id?: number; item_type: 'landing_page' | 'offer' | 'campaign' | 'direct_url' | 'direct'; item_id?: number; direct_url?: string; weight: number; item_group: 'lander' | 'offer'; }
export interface Path { id: number; name: string; weight: number; status: string; items: PathItem[]; campaign_id?: number; rule_id?: number; }
export interface Rule { id: number; name: string; priority: number; status: string; conditions: PathCondition[]; paths: Path[]; }

// --- COMPONENTE PRINCIPAL ---
const CampaignEditorPage: React.FC = () => {
    const { campaignId } = useParams<{ campaignId: string }>();
    const navigate = useNavigate();
    const isNewCampaign = campaignId === 'new';
    
    // --- ESTADO (Sin cambios, pero con adiciones) ---
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [defaultPaths, setDefaultPaths] = useState<Path[]>([]);
    const [rules, setRules] = useState<Rule[]>([]);
    const [offers, setOffers] = useState<Offer[]>([]);
    const [landingPages, setLandingPages] = useState<LandingPage[]>([]);
    const [allCampaigns, setAllCampaigns] = useState<Campaign[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    // NUEVO: Estado para gestionar la vista Maestro-Detalle
    const [activeTab, setActiveTab] = useState(0); // 0 para 'Default Paths', 1 para 'Rules'
    const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);

    // --- Lógica de carga de datos (Sin cambios) ---
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [offersRes, lpRes, allCampaignsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/offers`),
                axios.get(`${API_BASE_URL}/landing-pages`),
                axios.get(`${API_BASE_URL}/campaigns`)
            ]);
            setOffers(offersRes.data);
            setLandingPages(lpRes.data);

            if (!isNewCampaign && campaignId) {
                const [campaignRes, pathsRes, rulesRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/campaigns/${campaignId}`),
                    axios.get(`${API_BASE_URL}/paths/for-campaign/${campaignId}`),
                    axios.get(`${API_BASE_URL}/rules/for-campaign/${campaignId}`),
                ]);
                setCampaign(campaignRes.data);
                setDefaultPaths(pathsRes.data);
                setRules(rulesRes.data);
                setAllCampaigns(allCampaignsRes.data.filter((c: Campaign) => String(c.id) !== campaignId));
                
                // NUEVO: Seleccionar la primera regla por defecto si existe
                if (rulesRes.data.length > 0) {
                    setSelectedRuleId(rulesRes.data[0].id);
                }
            } else {
                 setAllCampaigns(allCampaignsRes.data);
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: 'Error al cargar los datos del funnel.' });
        } finally {
            setIsLoading(false);
        }
    }, [campaignId, isNewCampaign]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        
        if (result.type === 'RULES') {
            const items = Array.from(rules);
            const [reorderedItem] = items.splice(result.source.index, 1);
            items.splice(result.destination.index, 0, reorderedItem);
            // Actualizar la prioridad basada en el nuevo orden
            const updatedRules = items.map((rule, index) => ({ ...rule, priority: index }));
            setRules(updatedRules);
        }
    };

    // --- Manejadores de eventos (Ligeramente modificados o nuevos) ---
    const handlePathChange = (updatedPath: Path, index: number) => {
        const newPaths = [...defaultPaths];
        newPaths[index] = updatedPath;
        setDefaultPaths(newPaths);
    };

    const addNewDefaultPath = () => {
        const newPath: any = {
            id: Date.now(), name: `Nuevo Path #${defaultPaths.length + 1}`, weight: 100, status: 'active', items: []
        };
        setDefaultPaths([...defaultPaths, newPath]);
    };

    const deleteDefaultPath = (index: number) => {
        const newPaths = [...defaultPaths];
        newPaths.splice(index, 1);
        setDefaultPaths(newPaths);
    };
    
    const handleRuleChange = (updatedRule: Rule, index: number) => {
        const newRules = [...rules];
        newRules[index] = updatedRule;
        setRules(newRules);
    };

    const addNewRule = () => {
        const newRule: any = {
            id: Date.now(), name: `Nueva Regla #${rules.length + 1}`, priority: rules.length, status: 'active', conditions: [], paths: []
        };
        const newRules = [...rules, newRule];
        setRules(newRules);
        setSelectedRuleId(newRule.id); // NUEVO: Seleccionar la nueva regla automáticamente
    };
    
    const deleteRule = (index: number) => {
        if (selectedRuleId === rules[index].id) {
            setSelectedRuleId(null);
        }
        const newRules = [...rules];
        newRules.splice(index, 1);
        setRules(newRules);
    };
    
    const handleSave = async () => {
        if (isNewCampaign) {
            setNotification({ type: 'error', message: 'Primero debes crear la campaña.' });
            return;
        }
        if (!campaignId) return;
        
        setIsSaving(true);
        try {
            const payload = {
                defaultPaths: defaultPaths.map(p => ({ ...p, id: p.id > 1000000 ? undefined : p.id })),
                rules: rules.map(r => ({
                    ...r,
                    id: r.id > 1000000 ? undefined : r.id,
                    paths: r.paths.map(p => ({ ...p, id: p.id > 1000000 ? undefined : p.id }))
                }))
            };
            await axios.post(`${API_BASE_URL}/funnels/${campaignId}`, payload);
            setNotification({ type: 'success', message: 'Funnel guardado con éxito.' });
            fetchData(); // Recargar datos para obtener IDs de la BD
        } catch (err: any) {
            setNotification({ type: 'error', message: 'Error al guardar el funnel.' });
        } finally {
            setIsSaving(false);
        }
    };
    
    // NUEVO: Encontrar la regla seleccionada para pasarla al editor
    const selectedRule = rules.find(rule => rule.id === selectedRuleId);
    const selectedRuleIndex = rules.findIndex(rule => rule.id === selectedRuleId);

    if (isLoading) return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 10 }} />;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Snackbar open={!!notification} autoHideDuration={4000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>{notification?.message}</Alert>
            </Snackbar>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate('/campaigns')}><ArrowBackIcon /></IconButton>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                        {isNewCampaign ? 'Creando Nuevo Funnel' : `Editando Funnel: ${campaign?.name || ''}`}
                    </Typography>
                </Stack>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={isSaving}>
                    {isSaving ? <CircularProgress size={24} /> : 'Guardar Cambios'}
                </Button>
            </Stack>

            {/* NUEVO: Sistema de Pestañas para separar Default Paths de Rules */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label="Default Paths" />
                    <Tab label="Rules" />
                </Tabs>
            </Box>

            {/* VISTA PARA DEFAULT PATHS (Pestaña 0) */}
            {activeTab === 0 && (
                <Box>
                    <Typography variant="h5" gutterBottom>Paths por Defecto</Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>Estos paths se usarán si ninguna regla coincide con el tráfico entrante.</Typography>
                    {defaultPaths.map((path, index) => (
                        <Box key={path.id} sx={{ mb: 2 }}>
                            <PathBuilder pathData={path} offers={offers} landingPages={landingPages} campaigns={allCampaigns}
                                onChange={(updatedPath) => handlePathChange(updatedPath, index)}
                                onDelete={() => deleteDefaultPath(index)}
                                dragHandleProps={null} />
                        </Box>
                    ))}
                    <Button variant="outlined" startIcon={<AddCircleIcon />} size="small" onClick={addNewDefaultPath}>Añadir Path por Defecto</Button>
                </Box>
            )}

            {/* VISTA PARA RULES (Pestaña 1) - DISEÑO MAESTRO-DETALLE */}
            {activeTab === 1 && (
                <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 280px)' }}> {/* Ajustar altura según sea necesario */}
                    
                    {/* COLUMNA IZQUIERDA (Maestro) */}
                    <Paper sx={{ width: '35%', p: 2, overflowY: 'auto' }} elevation={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                             <Typography variant="h6">Orden de Reglas</Typography>
                             <Button variant="contained" size="small" startIcon={<AddCircleIcon />} onClick={addNewRule}>Nueva Regla</Button>
                        </Stack>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="rules-droppable" type="RULES">
                                {(provided) => (
                                    <Stack {...provided.droppableProps} ref={provided.innerRef} spacing={1.5}>
                                        {rules.map((rule, index) => (
                                            <Draggable key={rule.id} draggableId={String(rule.id)} index={index}>
                                                {(provided) => (
                                                    <Box ref={provided.innerRef} {...provided.draggableProps}>
                                                        <RuleListItem
                                                            rule={rule}
                                                            isSelected={selectedRuleId === rule.id}
                                                            onClick={() => setSelectedRuleId(rule.id)}
                                                            dragHandleProps={provided.dragHandleProps}
                                                        />
                                                    </Box>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </Stack>
                                )}
                            </Droppable>
                        </DragDropContext>
                        {rules.length === 0 && <Typography sx={{ mt: 3, textAlign: 'center' }} color="text.secondary">No hay reglas. ¡Añade una para empezar!</Typography>}
                    </Paper>

                    {/* COLUMNA DERECHA (Detalle) */}
                    <Paper sx={{ flex: 1, p: 3, overflowY: 'auto' }} elevation={2}>
                        {selectedRule && selectedRuleIndex !== -1 ? (
                            <RuleBuilder
                                key={selectedRule.id} // Key para forzar el remontaje al cambiar de regla
                                ruleData={selectedRule}
                                offers={offers}
                                landingPages={landingPages}
                                campaigns={allCampaigns}
                                onChange={(updatedRule) => handleRuleChange(updatedRule, selectedRuleIndex)}
                                onDelete={() => deleteRule(selectedRuleIndex)}
                                dragHandleProps={null} // El drag handle ahora está en la lista
                            />
                        ) : (
                            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                                <Typography variant="h6" color="text.secondary">Selecciona una regla para editarla</Typography>
                                <Typography color="text.secondary">o crea una nueva.</Typography>
                            </Stack>
                        )}
                    </Paper>
                </Box>
            )}
        </Container>
    );
};

export default CampaignEditorPage;